import { MessageDescriptor } from 'react-intl'

import { getProviderCancellationMessages } from './messages'
import { ProviderTypeAbbreviated } from '../providers/constants'

/**
 * Determines the appropriate cancellation consent message to display during appointment booking or
 * in the Client Intake based on health plan requirements, cancellation policies, and session limits.
 *
 * @param params - Configuration object containing booking and cancellation parameters
 * @param params.isHPIRequiredForAppointmentBooking - Whether Health Plan Information is required for booking
 * @param params.cancellationLimit - Maximum number of allowed cancellations as a string
 * @param params.providerType - Type of healthcare provider (optional)
 * @param params.sessionLimit - Maximum number of sessions allowed (optional)
 * @param params.cancellationsIncludedInSessionLimits - Whether cancellations count toward session limits
 *
 * @returns MessageDescriptor object containing the appropriate consent message for the user's situation
 *
 * @example
 * ```typescript
 * const message = getBookingCancellationConsentMessage({
 *   isHPIRequiredForAppointmentBooking: false,
 *   cancellationLimit: '2',
 *   providerType: 'THERAPIST',
 *   sessionLimit: 8,
 *   cancellationsIncludedInSessionLimits: true
 * });
 * ```
 */
export const getBookingCancellationConsentMessage = ({
  isHPIRequiredForAppointmentBooking,
  cancellationLimit,
  providerType,
  sessionLimit,
  cancellationsIncludedInSessionLimits,
  infractionMessagingDisabled,
  healthPlanExtensionEnabled,
  hasTriaged = true,
}: {
  isHPIRequiredForAppointmentBooking: boolean
  cancellationLimit: string
  providerType?: ProviderTypeAbbreviated
  sessionLimit?: number
  cancellationsIncludedInSessionLimits: boolean
  infractionMessagingDisabled: boolean
  healthPlanExtensionEnabled: boolean
  hasTriaged?: boolean
}): MessageDescriptor | string => {
  const PROVIDER_MESSAGES = getProviderCancellationMessages({
    providerType,
    stringOnlyMessage: !hasTriaged,
    cancellationLimit,
  })

  if (isHPIRequiredForAppointmentBooking && healthPlanExtensionEnabled && infractionMessagingDisabled) {
    return PROVIDER_MESSAGES.NON_DEDUCTIBLE_SESSIONS.INFRACTION_DISABLED_BUT_HPI_REQUIRED
  } else if (isHPIRequiredForAppointmentBooking || !cancellationLimit || cancellationLimit === '0') {
    return PROVIDER_MESSAGES.NON_DEDUCTIBLE_SESSIONS.NO_INFRACTIONS_COVERED_OR_HPI
  } else if (parseInt(cancellationLimit || '0') === (sessionLimit || 0) && cancellationsIncludedInSessionLimits) {
    return PROVIDER_MESSAGES.DEDUCTIBLE_SESSIONS.ALL_INFRACTIONS_COVERED
  }

  if (cancellationLimit === '1') {
    if (cancellationsIncludedInSessionLimits) {
      return PROVIDER_MESSAGES.DEDUCTIBLE_SESSIONS.ONLY_FIRST_INFRACTION_COVERED
    } else {
      return PROVIDER_MESSAGES.NON_DEDUCTIBLE_SESSIONS.ONLY_FIRST_INFRACTION_COVERED
    }
    // cancellationLimit > 1
  } else {
    if (cancellationsIncludedInSessionLimits) {
      return PROVIDER_MESSAGES.DEDUCTIBLE_SESSIONS.MULTIPLE_INFRACTIONS_COVERED
    } else {
      return PROVIDER_MESSAGES.NON_DEDUCTIBLE_SESSIONS.MULTIPLE_INFRACTIONS_COVERED
    }
  }
}

/**
 * Converts a string representation of session count to a numeric session limit.
 * Used to parse session limits from health plan configurations or user inputs.
 * Safely handles null, undefined, or invalid string values by defaulting to 0.
 *
 * @param numberOfSessions - String representation of the number of sessions allowed (optional)
 *                          Typically comes from health plan benefit configurations
 *
 * @returns Parsed integer value of the session limit, or 0 for null/undefined/invalid inputs
 *
 * @example
 * ```typescript
 * // Valid numeric strings
 * getSessionLimit('8')     // returns 8
 * getSessionLimit('12')    // returns 12
 *
 * // Edge cases - all return 0
 * getSessionLimit(undefined)  // returns 0
 * getSessionLimit(null)       // returns 0
 * getSessionLimit('')         // returns 0 (empty string is NaN)
 * getSessionLimit('abc')      // returns 0 (non-numeric string)
 * ```
 */
export const getSessionLimit = (numberOfSessions?: string) => {
  if (numberOfSessions == null || isNaN(Number(numberOfSessions))) {
    return 0
  }

  return parseInt(numberOfSessions)
}
